import { createFileRoute } from "@tanstack/react-router";
import {
  Headphones,
  Shield,
  HeartHandshake,
  Truck,
  Snowflake,
  Package,
  BadgeCheck,
} from "lucide-react";
import { InnerPage } from "@/components/InnerPage";
import { SectionHeader } from "@/components/SectionHeader";
import { StatsBar } from "@/components/StatsBar";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { pageHead } from "@/lib/seo";
import { breadcrumbsFor } from "@/lib/schema";
import { truckImage } from "@/lib/images";
import { SITE } from "@/lib/site";

const PATH = "/about";

const META_DESCRIPTION = `Family-run New Jersey freight carrier since ${SITE.foundingYear}. Drivers who care, dispatch that answers, and coast-to-coast produce and dry freight.`;

export const Route = createFileRoute("/about")({
  head: () =>
    pageHead({
      title: "About Fox Trucking | Family-Run NJ Freight Carrier Since 2012",
      description: META_DESCRIPTION,
      path: PATH,
      ogTitle: "About Fox Trucking | Family-Run NJ Freight Carrier Since 2012",
      ogDescription: META_DESCRIPTION,
    }),
  component: AboutPage,
});

const VALUES = [
  {
    i: HeartHandshake,
    t: "Drivers who care",
    d: "The people behind the wheel treat every load like it belongs to someone they know — because reputation is earned one delivery at a time.",
  },
  {
    i: Headphones,
    t: "Dispatch that answers",
    d: "Real humans pick up 24/7. Updates before you chase them. No bots, no ticket queues.",
  },
  {
    i: Shield,
    t: "Reliability over excuses",
    d: "Pricing fluctuates. Our commitment doesn’t. We say what we’ll do — then we do it.",
  },
] as const;

function AboutPage() {
  return (
    <InnerPage
      hero={{
        eyebrow: `Family-run · Since ${SITE.foundingYear}`,
        title: (
          <>
            About <span className="text-accent">Fox Trucking</span>
          </>
        ),
        intro: `A New Jersey freight carrier built on relationships — hauling produce, dry goods, and general freight coast to coast since ${SITE.foundingYear}.`,
        primaryCta: { label: "Book a Load", href: "/contact" },
        secondaryCta: { label: "Drive With Us", href: "/drive-with-us" },
      }}
      breadcrumbs={breadcrumbsFor("About", PATH)}
      showBottomCta
      bottomCta={{
        title: "Work with a carrier that picks up.",
        subtitle: "Book freight or apply to drive — same dispatch team, same phone that answers.",
        bookLabel: "Book a Load",
        bookHref: "/contact",
      }}
    >
      {/* OUR STORY */}
      <section className="relative bg-secondary py-24 text-secondary-foreground">
        <div className="absolute inset-0 bg-noise opacity-60" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 lg:grid-cols-2 lg:px-8">
          <div>
            <div className="relative aspect-[3/4] overflow-hidden rounded-lg border-4 border-accent">
              <ResponsiveImage
                {...truckImage}
                alt="Fox Trucking blue Volvo with reefer trailer"
                pictureClassName="absolute inset-0 h-full w-full"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div>
            <SectionHeader
              tone="dark"
              eyebrow="Our story"
              title={`Since ${SITE.foundingYear} in New Jersey.`}
              subtitle={`Founded in New Jersey in ${SITE.foundingYear} by ${SITE.founderName}, ${SITE.shortName} has spent over a decade building a name on one thing: doing what we say we'll do.`}
            />
            <p className="mt-6 text-sm leading-6 text-secondary-foreground/75 md:text-base">
              {/* TODO: Expand with a confirmed founder narrative from Fox Trucking. */}
              [FOUNDER STORY — placeholder]. Until then, the story on the road is the one shippers
              already know: on-time freight, elite communication, and a team that treats your cargo
              like their livelihood.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE STAND FOR */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Values"
            title="What we stand for."
            subtitle="Drivers who care. Dispatch that answers. A family-run carrier that still picks up the phone."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {VALUES.map((v) => (
              <div
                key={v.t}
                className="rounded-lg border border-border bg-card p-8 transition hover:border-primary"
              >
                <div className="grid h-12 w-12 place-items-center rounded-md bg-primary text-primary-foreground">
                  <v.i className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="mt-5 font-display text-2xl text-card-foreground">{v.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FLEET AT A GLANCE */}
      <section className="bg-muted py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Fleet"
            title="Fleet at a glance."
            subtitle="Equipment details below are placeholders until Fox Trucking confirms public figures."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            <FleetCard
              icon={Truck}
              title="Tractors"
              // TODO: Confirm public truck count.
              value="[TRUCK COUNT]"
            />
            <FleetCard
              icon={Snowflake}
              title="Reefer trailers"
              // TODO: Confirm reefer trailer types / counts.
              value="[TRAILER TYPES — reefer]"
            />
            <FleetCard
              icon={Package}
              title="Dry van trailers"
              // TODO: Confirm dry van trailer types / counts.
              value="[TRAILER TYPES — dry van]"
            />
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeader
            eyebrow="Credentials"
            title="Authority & identifiers."
            subtitle="Official numbers will appear here once confirmed — no invented USDOT or MC numbers."
          />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:max-w-3xl">
            {/* TODO: Replace with confirmed USDOT number. */}
            <CredentialRow icon={BadgeCheck} label="USDOT #" value="[USDOT #]" />
            {/* TODO: Replace with confirmed MC number. */}
            <CredentialRow icon={BadgeCheck} label="MC #" value="[MC #]" />
          </div>
          <p className="mt-6 max-w-2xl text-sm text-muted-foreground">
            Based in {SITE.location}. Call{" "}
            <a
              href={`tel:${SITE.phoneTel}`}
              className="text-primary underline-offset-2 hover:underline"
            >
              {SITE.phoneDisplay}
            </a>{" "}
            for carrier packet requests.
          </p>
        </div>
      </section>

      {/* STATS */}
      <StatsBar variant="section" />

      {/* DUAL CTA */}
      <section className="bg-background py-24">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 md:grid-cols-2 lg:px-8">
          <a
            href="/contact"
            className="group rounded-lg border border-border bg-card p-8 transition hover:border-primary"
          >
            <p className="font-display text-xs tracking-[0.3em] text-primary">Shippers</p>
            <h3 className="mt-3 font-display text-3xl text-card-foreground">Book a Load</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Request a quote for refrigerated or dry van freight — dispatch is available 24/7.
            </p>
            <span className="mt-6 inline-flex font-display tracking-wider text-primary group-hover:underline">
              Go to Book a Load →
            </span>
          </a>
          <a
            href="/drive-with-us"
            className="group rounded-lg border border-border bg-card p-8 transition hover:border-primary"
          >
            <p className="font-display text-xs tracking-[0.3em] text-primary">Drivers</p>
            <h3 className="mt-3 font-display text-3xl text-card-foreground">Drive With Us</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Class A CDL careers with a carrier where dispatch treats you like a person.
            </p>
            <span className="mt-6 inline-flex font-display tracking-wider text-primary group-hover:underline">
              See driver careers →
            </span>
          </a>
        </div>
      </section>
    </InnerPage>
  );
}

function FleetCard({
  icon: Icon,
  title,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: string;
}) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-card p-8">
      <Icon className="h-8 w-8 text-primary" aria-hidden="true" />
      <h3 className="mt-4 font-display text-xl text-card-foreground">{title}</h3>
      <p className="mt-2 font-mono text-sm text-muted-foreground">{value}</p>
    </div>
  );
}

function CredentialRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-4 rounded-lg border border-border bg-card px-5 py-4">
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <div>
        <div className="font-display text-xs tracking-widest text-muted-foreground">{label}</div>
        <div className="mt-0.5 font-mono text-lg text-card-foreground">{value}</div>
      </div>
    </div>
  );
}
